import React, { useMemo, useState, useEffect } from 'react';
import type { AuthUser } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import type { FilteredViewCategory, ViewId } from '../config/navConfig';
import { isAtLeast } from '../utils/roles';
import { isCustomerRole } from '../config/navConfig';
import { BRAND } from '../config/brand';
import { EventSwitcher } from './EventSwitcher';
import { NavViewGroup } from './NavViewGroup';
import { DashboardNavSubmenu } from './DashboardNavSubmenu';
import { useDashboardNavConfig } from '../hooks/useDashboardNavConfig';
import { useFocusedNav } from '../hooks/useFocusedNav';
import { apiClient } from '../api/client';

export interface DesktopSidebarProps {
  user: AuthUser | null;
  navViews: FilteredViewCategory[];
  collapsedViews: Set<ViewId>;
  onToggleView: (viewId: ViewId) => void;
  onLogout: () => void;
}

/**
 * Desktop sidebar component extracted from AppLayout.
 * Renders brand header, user info, navigation view groups, and footer.
 */
export function DesktopSidebar({
  user,
  navViews,
  collapsedViews,
  onToggleView,
  onLogout,
}: DesktopSidebarProps) {
  const { isDark, toggleTheme } = usePreferences();
  const { config: dashboardNavConfig } = useDashboardNavConfig();
  const focusedNav = useFocusedNav();
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [discussionsUnread, setDiscussionsUnread] = useState(0);

  // Fetch pending budget submissions count for the review badge
  useEffect(() => {
    async function fetchPendingCount() {
      if (!isAtLeast(user?.role || '', 'Organizer')) return;
      try {
        const submissions = await apiClient<{ status: string }[]>('/budget-submissions');
        const pending = submissions.filter(
          (s) => s.status === 'Submitted' || s.status === 'InReview'
        );
        setPendingReviewCount(pending.length);
      } catch {
        // Non-critical — badge just won't show
      }
    }
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(interval);
  }, [user?.role]);

  // Poll unread notification count every 60 seconds (lightweight, just for badge)
  useEffect(() => {
    async function fetchUnreadCount() {
      try {
        const data = await apiClient<{ unreadCount: number }>('/announcements/unread-count');
        setUnreadNotifCount(data.unreadCount);
      } catch {
        // Non-critical
      }
    }
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, []);

  // Poll discussions unread count (client-side localStorage tracking)
  useEffect(() => {
    async function fetchDiscussionsUnread() {
      if (!isAtLeast(user?.role || '', 'Delegate')) return;
      try {
        const categories = await apiClient<Array<{ categoryId: string; latestTopic: { createdAt: string } | null }>>('/comm-portal/integrated/my-categories');
        const STORAGE_KEY = 'kdu-discussions-last-read';
        let lastReads: Record<string, string> = {};
        try { lastReads = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch {}
        let unread = 0;
        for (const cat of categories) {
          if (cat.latestTopic) {
            const lastRead = lastReads[cat.categoryId];
            if (!lastRead || new Date(cat.latestTopic.createdAt) > new Date(lastRead)) {
              unread++;
            }
          }
        }
        setDiscussionsUnread(unread);
      } catch { /* Non-critical */ }
    }
    fetchDiscussionsUnread();
    const interval = setInterval(fetchDiscussionsUnread, 90000);
    return () => clearInterval(interval);
  }, [user?.role]);

  // Use focused nav views when the hook has data, otherwise fall back to the navViews prop.
  // EXCEPTION: customer-tier (spinoff) roles always use the module-aware navViews
  // prop — the backend focused-nav resolver is not module-aware and would
  // re-expand module-gated items. Non-blocking for everyone else.
  const filteredNavViews = useMemo(() => {
    if (isCustomerRole(user?.role)) {
      return navViews;
    }
    if (!focusedNav.loading && focusedNav.navViews.length > 0) {
      return focusedNav.navViews;
    }
    return navViews;
  }, [navViews, focusedNav.navViews, focusedNav.loading, user?.role]);

  return (
    <aside style={sidebarStyles.sidebar}>
      {/* Brand — prominent people-mark + EJTHub wordmark */}
      <a
        href={BRAND.marketingUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{ ...sidebarStyles.brand, textDecoration: 'none' }}
        title={`Go to ${BRAND.marketingUrl.replace('https://', '')}`}
        aria-label={`Open ${BRAND.name} marketing site in a new tab`}
      >
        <img
          src={BRAND.markWhite}
          alt=""
          aria-hidden="true"
          style={{ height: 32, width: 'auto', objectFit: 'contain', flexShrink: 0, display: 'block' }}
        />
        <span style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.01em', color: '#ffffff' }}>
          {BRAND.name}
        </span>
      </a>

      {/* User Info & Sign Out */}
      <div style={sidebarStyles.topUserSection}>
        <div style={sidebarStyles.userInfo}>
          <div style={sidebarStyles.avatar}>
            {user?.email?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={sidebarStyles.userName}>
              {user?.fullName || user?.email?.split('@')[0] || 'User'}
            </div>
            <div style={sidebarStyles.userRole}>
              {user?.role || 'Attendee'}
            </div>
          </div>
          <button style={sidebarStyles.signOutBtnCompact} onClick={onLogout}>
            Sign Out
          </button>
        </div>
      </div>

      {/* Active-event picker — scopes all data pages to the selected event */}
      {isAtLeast(user?.role || '', 'CustomerEditor') && <EventSwitcher />}

      {/* Focused Mode Indicator */}
      {focusedNav.focusedModeActive && !focusedNav.escapeHatchActive && user?.role !== 'Admin' && (
        <div style={sidebarStyles.focusedBanner}>
          <div style={sidebarStyles.focusedBannerIcon}>🎯</div>
          <div>
            <div style={sidebarStyles.focusedBannerTitle}>Focused View</div>
            {focusedNav.activeGroupNames.length > 0 && (
              <div style={sidebarStyles.focusedBannerGroups}>
                {focusedNav.activeGroupNames.join(' · ')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Navigation View Groups */}
      <nav style={sidebarStyles.nav}>
        {/* Dashboard Nav Submenu — at top when admin-enabled */}
        {dashboardNavConfig.enabled && (
          <DashboardNavSubmenu label={dashboardNavConfig.label} userRole={user?.role} />
        )}

        {/* View category groups */}
        {filteredNavViews.map((viewCategory) => {
          // Inject Budget Review link into planning category items when pending count > 0
          let items =
            viewCategory.category.id === 'planning' && pendingReviewCount > 0
              ? [
                  ...viewCategory.items,
                  {
                    path: '/budget-submissions/review',
                    label: 'Budget Review',
                    icon: '✅',
                    viewId: 'planning' as ViewId,
                    badge: pendingReviewCount,
                  },
                ]
              : [...viewCategory.items];

          // Inject unread notification badge on the Announcements nav item
          if (unreadNotifCount > 0) {
            items = items.map((item) =>
              item.path === '/announcements'
                ? { ...item, badge: unreadNotifCount }
                : item
            );
          }

          // Inject discussions unread badge
          if (discussionsUnread > 0) {
            items = items.map((item) =>
              item.path === '/discussions'
                ? { ...item, badge: discussionsUnread }
                : item
            );
          }

          return (
            <NavViewGroup
              key={viewCategory.category.id}
              view={viewCategory.category}
              items={items}
              isCollapsed={collapsedViews.has(viewCategory.category.id)}
              onToggle={() => onToggleView(viewCategory.category.id)}
            />
          );
        })}

        {/* Focused Nav escape hatch links */}
        {focusedNav.focusedModeActive && !focusedNav.escapeHatchActive && user?.role !== 'Admin' && (
          <button
            style={sidebarStyles.escapeHatchLink}
            onClick={focusedNav.toggleEscapeHatch}
          >
            <span style={sidebarStyles.escapeHatchIcon}>↗</span>
            View All Pages
          </button>
        )}
        {focusedNav.escapeHatchActive && (
          <button
            style={sidebarStyles.escapeHatchLink}
            onClick={focusedNav.toggleEscapeHatch}
          >
            <span style={sidebarStyles.escapeHatchIcon}>←</span>
            Return to Focused View
          </button>
        )}

      </nav>

      {/* Footer */}
      <div style={sidebarStyles.footer}>
        <button
          style={sidebarStyles.themeToggle}
          onClick={toggleTheme}
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? '☀️' : '🌙'} {isDark ? 'Light Mode' : 'Dark Mode'}
        </button>
      </div>
    </aside>
  );
}


const sidebarStyles: Record<string, React.CSSProperties> = {
  sidebar: {
    width: '260px',
    backgroundColor: '#0a1f14',
    borderRight: '1px solid rgba(255,255,255,0.08)',
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    overflowY: 'auto',
    zIndex: 100,
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 12px',
    marginBottom: '12px',
  },
  logoImg: {
    width: '44px',
    height: '44px',
    borderRadius: '10px',
    objectFit: 'contain' as const,
    backgroundColor: 'rgba(255,255,255,0.05)',
    padding: '2px',
  },
  brandTitle: {
    fontSize: '16px',
    fontWeight: 700,
    color: '#f0f4f1',
    lineHeight: '1.2',
  },
  brandSub: {
    fontSize: '11px',
    color: '#c9a84c',
    fontWeight: 500,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  topUserSection: {
    padding: '0 4px 16px',
    marginBottom: '16px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 4px',
    marginBottom: '10px',
  },
  avatar: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #1a6b3c, #2a8f54)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: 700,
    color: 'white',
  },
  userName: {
    fontSize: '13px',
    color: '#f0f4f1',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  userRole: {
    fontSize: '11px',
    color: '#6b8572',
  },
  signOutBtnCompact: {
    padding: '5px 10px',
    backgroundColor: 'rgba(255,82,82,0.1)',
    border: '1px solid rgba(255,82,82,0.3)',
    borderRadius: '5px',
    color: '#ff8a80',
    fontSize: '11px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
    whiteSpace: 'nowrap' as const,
  },
  nav: {
    flex: 1,
  },
  quickAccess: {
    marginBottom: '16px',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#a3b8a9',
    fontSize: '14px',
    fontWeight: 500,
    textDecoration: 'none',
    transition: 'all 0.2s',
    position: 'relative' as const,
    border: '1px solid transparent',
  },
  navItemActive: {
    background: 'linear-gradient(135deg, rgba(26,107,60,0.2), rgba(201,168,76,0.1))',
    color: '#c9a84c',
    border: '1px solid rgba(201,168,76,0.2)',
  },
  navIcon: {
    fontSize: '18px',
    width: '24px',
    textAlign: 'center' as const,
  },
  footer: {
    marginTop: 'auto',
    paddingTop: '16px',
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  themeToggle: {
    width: '100%',
    padding: '8px',
    backgroundColor: 'rgba(201,168,76,0.08)',
    border: '1px solid rgba(201,168,76,0.2)',
    borderRadius: '6px',
    color: '#c9a84c',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    marginBottom: '8px',
  },
  escapeHatchLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    marginTop: '12px',
    background: 'none',
    border: 'none',
    borderTop: '1px solid rgba(255,255,255,0.06)',
    color: '#6b8572',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'color 0.2s',
    width: '100%',
    textAlign: 'left' as const,
  },
  escapeHatchIcon: {
    fontSize: '13px',
    opacity: 0.7,
  },
  focusedBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    marginBottom: '12px',
    backgroundColor: 'rgba(201,168,76,0.08)',
    border: '1px solid rgba(201,168,76,0.2)',
    borderRadius: '8px',
  },
  focusedBannerIcon: {
    fontSize: '16px',
    flexShrink: 0,
  },
  focusedBannerTitle: {
    fontSize: '11px',
    fontWeight: 700,
    color: '#c9a84c',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
  },
  focusedBannerGroups: {
    fontSize: '11px',
    color: '#a3b8a9',
    marginTop: '2px',
    lineHeight: 1.3,
  },
};
